import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import { useTableContext } from "./table.context"

export type TablePaginationProps = ComponentProps<"div"> & {
  /** Page size options. Defaults to [25, 50, 100, 250]. */
  pageSizeOptions?: number[]
  label?: string
}

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 250]

/**
 * Pagination controls for client-side paginated tables. Requires the parent
 * `<Table enablePagination />`. Renders nothing when there is only one page.
 */
function TablePagination(props: TablePaginationProps) {
  const {
    className,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    label = "rows",
    ...divProps
  } = props
  const { table } = useTableContext()

  const filteredCount = table.getFilteredRowModel().rows.length
  const { pageIndex, pageSize } = table.getState().pagination
  const pageCount = Math.max(1, table.getPageCount())

  if (filteredCount === 0) return null

  const start = pageIndex * pageSize + 1
  const end = Math.min(filteredCount, (pageIndex + 1) * pageSize)

  return (
    <div className={cn("ui-table-pagination", className)} {...divProps}>
      <div className="ui-table-pagination-summary">
        Showing <span className="tabular-nums">{start}</span>–
        <span className="tabular-nums">{end}</span> of{" "}
        <span className="tabular-nums">{filteredCount}</span> {label}
      </div>

      <label className="ui-table-pagination-page-size">
        Rows
        <select
          className="ui-table-pagination-select"
          value={pageSize}
          onChange={(e) => table.setPageSize(Number(e.target.value))}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>

      <div className="ui-table-pagination-controls">
        <Button
          variant="ghost"
          size="sm"
          iconSrc="angle-double-left"
          iconSize={12}
          isSquare
          aria-label="First page"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.setPageIndex(0)}
        />
        <Button
          variant="ghost"
          size="sm"
          iconSrc="angle-left"
          iconSize={12}
          isSquare
          aria-label="Previous page"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
        />
        <span className="ui-table-pagination-page">
          Page <span className="tabular-nums">{pageIndex + 1}</span> of{" "}
          <span className="tabular-nums">{pageCount}</span>
        </span>
        <Button
          variant="ghost"
          size="sm"
          iconSrc="angle-right"
          iconSize={12}
          isSquare
          aria-label="Next page"
          disabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
        />
        <Button
          variant="ghost"
          size="sm"
          iconSrc="angle-double-right"
          iconSize={12}
          isSquare
          aria-label="Last page"
          disabled={!table.getCanNextPage()}
          onClick={() => table.setPageIndex(pageCount - 1)}
        />
      </div>
    </div>
  )
}

export default TablePagination
